import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { getAllAIF } from "../../redux/aif/aifSlice";
import {
  addListingData,
  deleteListingData,
} from "../../redux/admin/adminSlice";
import toast from "react-hot-toast";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import AppLoader from "../../components/AppLoader/AppLoader";
import {
  AddListingButton,
  bodyCellSx,
  BORDER,
  DeleteAction,
  DetailInfoGrid,
  DETAIL_CARD_BG,
  EmptyTableRow,
  ExpandButton,
  formatListingDate,
  headCellSx,
  ListingAvatar,
  ListingPageShell,
  ReturnValue,
  schemeHeadCellSx,
  SortableHeader,
} from "../../components/AssetListing/assetListingShared";
import { DARK_NAVY, DARK_NAVY_LIGHT, PROFILE_FONT } from "../../constants/layout";

const AIFTableRow = ({ data, onDelete }: { data: any; onDelete: (id: number) => void }) => {
  const [open, setOpen] = useState(false);
  const userType = Number(localStorage.getItem("userType")) || 0;

  const isValidAssetName = (assetName: string) =>
    assetName && assetName !== "-" && assetName.trim() !== "";

  const validSchemes = (data?.schemes || []).filter((scheme: any) =>
    isValidAssetName(scheme?.assetName)
  );

  if (!validSchemes.length) return null;

  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        sx={{ cursor: "pointer", bgcolor: "#FCFCFC", "&:hover": { bgcolor: "#F5F5F5" } }}
      >
        <TableCell sx={bodyCellSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ListingAvatar name={data.promoter} />
            <Box>
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, fontWeight: 600, color: DARK_NAVY }}>
                {data.promoter}
              </Typography>
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT, mt: 0.25 }}>
                {validSchemes.length} scheme{validSchemes.length > 1 ? "s" : ""}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={bodyCellSx} align="center">
          <ExpandButton open={open} onClick={() => setOpen(!open)} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={2} sx={{ py: 0, borderBottom: open ? `1px solid #F5F5F5` : "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: { xs: 1, md: 2 }, py: 1.5 }}>
              <TableContainer
                sx={{
                  borderRadius: "12px",
                  border: `1px solid ${BORDER}`,
                  bgcolor: DETAIL_CARD_BG,
                  overflow: "hidden",
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={schemeHeadCellSx}>Scheme Name</TableCell>
                      <TableCell sx={schemeHeadCellSx} align="center">
                        Category
                      </TableCell>
                      <TableCell sx={schemeHeadCellSx} align="center">
                        Fund Manager
                      </TableCell>
                      <TableCell sx={schemeHeadCellSx} align="center">
                        Inception
                      </TableCell>
                      <TableCell sx={schemeHeadCellSx} align="center">
                        AUM (Cr)
                      </TableCell>
                      <TableCell sx={schemeHeadCellSx} align="center">
                        1M Abs.
                      </TableCell>
                      <TableCell sx={schemeHeadCellSx} align="center">
                        1Y Ann.
                      </TableCell>
                      <TableCell sx={schemeHeadCellSx} align="center">
                        Since Inception
                      </TableCell>
                      {userType === 4 && (
                        <TableCell sx={{ ...schemeHeadCellSx, width: 72 }} align="center">
                          Actions
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validSchemes.map((scheme: any) => (
                      <TableRow key={scheme.id} hover sx={{ "&:hover": { bgcolor: "#FAFAFA" } }}>
                        <TableCell sx={{ ...bodyCellSx, py: 1.25 }}>
                          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, fontWeight: 600, color: DARK_NAVY }}>
                            {scheme.assetName}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, py: 1.25, color: DARK_NAVY_LIGHT }} align="center">
                          {scheme.category || "-"}
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, py: 1.25, color: DARK_NAVY_LIGHT }} align="center">
                          {scheme.fundManager || "-"}
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, py: 1.25, color: DARK_NAVY_LIGHT }} align="center">
                          {formatListingDate(scheme.inceptionDate)}
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, py: 1.25, color: DARK_NAVY_LIGHT }} align="center">
                          {scheme.aumInCr || "-"}
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, py: 1.25 }} align="center">
                          <ReturnValue value={scheme.abs1Month} />
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, py: 1.25 }} align="center">
                          <ReturnValue value={scheme.ann1Year} />
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, py: 1.25 }} align="center">
                          <ReturnValue value={scheme.returnSinceInception} />
                        </TableCell>
                        {userType === 4 && (
                          <TableCell sx={{ ...bodyCellSx, py: 1.25 }} align="center">
                            <DeleteAction onClick={() => onDelete(scheme.id)} />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {validSchemes.map((scheme: any) => (
                <Box key={`details-${scheme.id}`} sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, fontWeight: 600, color: DARK_NAVY_LIGHT, mb: 1 }}>
                    {scheme.assetName}
                  </Typography>
                  <DetailInfoGrid
                    items={[
                      { label: "Fixed Rate", value: scheme.fixedRate || "-" },
                      { label: "Hybrid Variable Rate", value: scheme.hybridVariableRate || "-" },
                      { label: "Exit Load", value: scheme.exitLoad || "-" },
                      { label: "Alpha", value: scheme.alpha || "-" },
                      { label: "Beta", value: scheme.beta || "-" },
                      { label: "Sharpe Ratio", value: scheme.sharpeRatio || "-" },
                    ]}
                  />
                </Box>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const AIFListing = () => {
  const dispatch = useDispatch<any>();
  const userType = Number(localStorage.getItem("userType")) || 0;
  const [loader, setLoader] = React.useState(true);
  const [totalEnteries, setTotalEnteries] = useState<number>();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [countOFPages, seCountOFPages] = useState(1);
  const [showAddAIFModal, setShowAddAIFModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number>(0);

  const [aifData, setAIFData] = useState<any[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: string | null;
  }>({
    key: "assetName", // Set the default key to "assetName" or any other column you want
    direction: "ascending", // Set the default direction to ascending
  });

  const [formData, setFormData] = useState<any>({
    amc: "",
    productName: "",
    category: "",
    aum: "",
    inceptionDate: "",
    fundManagers: "",
    fixed: "",
    hybridVariable: "",
    exitCharges: "",
    largeCap: "",
    midCap: "",
    smallCap: "",
    cash: "",
    oneM: "",
    threeM: "",
    sixM: "",
    oneY: "",
    twoY: "",
    threeY: "",
    fiveY: "",
    rsi: "",
    alpha: "",
    beta: "",
    sd: "",
    upCapture: "",
    downCapture: "",
    sharpRatio: "",
    sortino: "",
  });

  //Fetch MF Data from Get All fund list API
  const fetchData = async () => {
    setLoader(true); // Start loading
    try {
      const response = await dispatch(
        getAllAIF({ pageIndex: 1, pageSize: 1000 })
      );

      if (response) {
        let aifList = response?.payload?.aifList || [];

        // Filter based on query for schemeName, schemeType, schemePlan, and riskLevel
        // Filter out entries where assetName is missing or equal to "-"
        const filteredEntries = aifList.flatMap((item: any) =>
          item.schemes.filter(
            (scheme: any) => scheme.assetName && scheme.assetName !== "-"
          )
        );

        // Set the filtered length
        setTotalEnteries(filteredEntries.length);
        setAIFData(aifList);
        // setTotalFound(mfList?.length);
        seCountOFPages(response?.payload?.totalPages || 0);
      }
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setLoader(false); // End loading
      //   setfilterButton(false);
    }
  };

  useEffect(() => {
    setLoader(true);
    fetchData();
  }, []);

  useEffect(() => {
    // Filter the dataEnteries array based on the searchTerm
    const filtered = aifData?.filter((entry: any) => {
      return entry.schemes.some(
        (scheme: any) =>
          scheme.assetName.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          scheme.category.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          scheme.promoter.toLowerCase()?.includes(searchTerm.toLowerCase())
      );
    });
    setFilteredData(filtered || []);
  }, [searchTerm, aifData]);

  const resetState = () => {
    setFormData({
      amc: "",
      productName: "",
      category: "",
      aum: "",
      inceptionDate: "",
      fundManagers: "",
      fixed: "",
      hybridVariable: "",
      exitCharges: "",
      largeCap: "",
      midCap: "",
      smallCap: "",
      cash: "",
      oneM: "",
      threeM: "",
      sixM: "",
      oneY: "",
      twoY: "",
      threeY: "",
      fiveY: "",
      rsi: "",
      alpha: "",
      beta: "",
      sd: "",
      upCapture: "",
      downCapture: "",
      sharpRatio: "",
      sortino: "",
    });
    setShowAddAIFModal(false);
  };

  const getSortedData = useCallback(() => {
    if (!sortConfig.key) return filteredData;
    const sortedData = [...filteredData];

    const compareStrings = (a: string, b: string) => {
      if (a < b) return sortConfig.direction === "ascending" ? -1 : 1;
      if (a > b) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    };

    const compareNumbers = (a: number, b: number) => {
      return sortConfig.direction === "ascending" ? a - b : b - a;
    };

    const compareDates = (a: string, b: string) => {
      // Check if either date is invalid
      if (!a || !b) return 0; // You can handle this differently based on requirements

      // Split the date string into components
      const [dayA, monthA, yearA] = a.split("-")?.map(Number);
      const [dayB, monthB, yearB] = b.split("-")?.map(Number);

      // Validate the parsed components to ensure they form a valid date
      if (
        isNaN(dayA) ||
        isNaN(monthA) ||
        isNaN(yearA) ||
        isNaN(dayB) ||
        isNaN(monthB) ||
        isNaN(yearB)
      ) {
        return 0; // You can handle invalid dates differently
      }

      // Create Date objects
      const dateA = new Date(yearA, monthA - 1, dayA);
      const dateB = new Date(yearB, monthB - 1, dayB);

      // Compare dates
      return sortConfig.direction === "ascending"
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    };

    sortedData.sort((a, b) => {
      const schemeA = a.schemes?.[0] || {};
      const schemeB = b.schemes?.[0] || {};

      switch (sortConfig.key) {
        case "assetName":
          return compareStrings(
            a?.promoter.toUpperCase(),
            b?.promoter.toUpperCase()
          );
        case "inceptionDate":
          return compareDates(schemeA.inceptionDate, schemeB.inceptionDate);
        case "aumInCr":
        case "abs1Month":
        case "ann1Year":
        case "returnSinceInception":
          return compareNumbers(
            parseFloat(schemeA[sortConfig.key]),
            parseFloat(schemeB[sortConfig.key])
          );
        case "promoter":
          return compareStrings(
            a.promoter.toUpperCase(),
            b.promoter.toUpperCase()
          );
        default:
          return 0;
      }
    });

    return sortedData;
  }, [filteredData, sortConfig]);

  const handleSort = (key: string) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = getSortedData();

  const handleAddAIF = async () => {
    try {
      setLoader(true);
      const response = await dispatch(
        addListingData({
          listingType: 5,
          ...formData,
        })
      );
      if (response?.error?.message) {
        toast.error("Error adding stock.");
        return;
      }

      toast.success("Stock added successfully.");
      fetchData();
      resetState();
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoader(false);
    }
  };

  const onDelete = (id: number) => {
    setShowDeleteModal(true);
    setDeleteId(id);
  };

  const handleDeleteAIF = async () => {
    try {
      setLoader(true);
      const response = await dispatch(
        deleteListingData({
          listingType: 5,
          id: deleteId,
        })
      );
      if (response?.error?.message) {
        toast.error("Error deleting AIF.");
        return;
      }

      toast.success("AIF deleted successfully.");
      setShowDeleteModal(false);
      fetchData();
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      <AppLoader loading={loader} />
      <ListingPageShell
        title="Alternative Investment Fund (AIF)"
        subtitle="Browse AIF promoters and schemes with performance metrics."
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search AIF by name, category or promoter..."
        action={
          userType === 4 ? (
            <AddListingButton label="Add AIF" onClick={() => setShowAddAIFModal(true)} />
          ) : undefined
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>
                  <SortableHeader label="Promoter" onSort={() => handleSort("assetName")} />
                </TableCell>
                <TableCell sx={{ ...headCellSx, width: 72 }} align="center">
                  Details
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.length === 0 ? (
                <TableRow>
                  <EmptyTableRow colSpan={2} />
                </TableRow>
              ) : (
                sortedData.map((row, index) => (
                  <AIFTableRow key={index} data={row} onDelete={onDelete} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </ListingPageShell>
      <Modal open={showAddAIFModal} onClose={() => setShowAddAIFModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 800, // Adjust width as needed
            maxHeight: "80vh", // Limit the height of the modal
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            overflowY: "auto", // Make the content scrollable on the Y-axis
            borderRadius: 2,
          }}
        >
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h6" component="h2">
              {"Add AIF"}
            </Typography>
            <IconButton onClick={() => setShowAddAIFModal(false)}>
              <Close />
            </IconButton>
          </Box>
          <Box component="form" noValidate autoComplete="off">
            <div className="grid grid-cols-2 gap-4">
              <TextField
                label="AMC Name *"
                variant="outlined"
                fullWidth
                margin="normal"
                name="amc"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, amc: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.amc || ""}
              />
              <TextField
                label="Scheme Name *"
                variant="outlined"
                fullWidth
                margin="normal"
                name="productName"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, productName: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.productName || ""}
              />
              <TextField
                label="Category"
                variant="outlined"
                fullWidth
                margin="normal"
                name="category"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.category || ""}
              />
              <TextField
                label="AUM (in Cr)"
                variant="outlined"
                fullWidth
                margin="normal"
                name="aum"
                type="number"
                onChange={(e) =>
                  setFormData({ ...formData, aum: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.aum || ""}
              />
              <TextField
                label="Inception Date"
                variant="outlined"
                fullWidth
                margin="normal"
                name="inceptionDate"
                type="date"
                onChange={(e) =>
                  setFormData({ ...formData, inceptionDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.inceptionDate || ""}
              />
              <TextField
                label="Fund Manager"
                variant="outlined"
                fullWidth
                margin="normal"
                name="fundManagers"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, fundManagers: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.fundManagers || ""}
              />
              <TextField
                label="Fixed Rate"
                variant="outlined"
                fullWidth
                margin="normal"
                name="fixed"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, fixed: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.fixed || ""}
              />
              <TextField
                label="Hybrid Variable Rate"
                variant="outlined"
                fullWidth
                margin="normal"
                name="hybridVariable"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, hybridVariable: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.hybridVariable || ""}
              />
              <TextField
                label="Exit Load"
                variant="outlined"
                fullWidth
                margin="normal"
                name="exitCharges"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, exitCharges: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.exitCharges || ""}
              />
              <TextField
                label="Large Cap"
                variant="outlined"
                fullWidth
                margin="normal"
                name="largeCap"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, largeCap: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.largeCap || ""}
              />
              <TextField
                label="Mid Cap"
                variant="outlined"
                fullWidth
                margin="normal"
                name="midCap"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, midCap: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.midCap || ""}
              />
              <TextField
                label="Small Cap"
                variant="outlined"
                fullWidth
                margin="normal"
                name="smallCap"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, smallCap: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.smallCap || ""}
              />
              <TextField
                label="Cash"
                variant="outlined"
                fullWidth
                margin="normal"
                name="cash"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, cash: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.cash || ""}
              />
              <TextField
                label="1M Return"
                variant="outlined"
                fullWidth
                margin="normal"
                name="oneM"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, oneM: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.oneM || ""}
              />
              <TextField
                label="3M Return"
                variant="outlined"
                fullWidth
                margin="normal"
                name="threeM"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, threeM: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.threeM || ""}
              />
              <TextField
                label="6M Return"
                variant="outlined"
                fullWidth
                margin="normal"
                name="sixM"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, sixM: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.sixM || ""}
              />
              <TextField
                label="1Y Return"
                variant="outlined"
                fullWidth
                margin="normal"
                name="oneY"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, oneY: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.oneY || ""}
              />
              <TextField
                label="2Y Return"
                variant="outlined"
                fullWidth
                margin="normal"
                name="twoY"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, twoY: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.twoY || ""}
              />
              <TextField
                label="3Y Return"
                variant="outlined"
                fullWidth
                margin="normal"
                name="threeY"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, threeY: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.threeY || ""}
              />
              <TextField
                label="5Y Return"
                variant="outlined"
                fullWidth
                margin="normal"
                name="fiveY"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, fiveY: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.fiveY || ""}
              />
              <TextField
                label="Return Since Inception"
                variant="outlined"
                fullWidth
                margin="normal"
                name="rsi"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, rsi: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.rsi || ""}
              />
              <TextField
                label="Alpha"
                variant="outlined"
                fullWidth
                margin="normal"
                name="alpha"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, alpha: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.alpha || ""}
              />
              <TextField
                label="Beta"
                variant="outlined"
                fullWidth
                margin="normal"
                name="beta"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, beta: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.beta || ""}
              />
              <TextField
                label="SD"
                variant="outlined"
                fullWidth
                margin="normal"
                name="sd"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, sd: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.sd || ""}
              />
              <TextField
                label="Up Capture"
                variant="outlined"
                fullWidth
                margin="normal"
                name="upCapture"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, upCapture: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.upCapture || ""}
              />
              <TextField
                label="Down Capture"
                variant="outlined"
                fullWidth
                margin="normal"
                name="downCapture"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, downCapture: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.downCapture || ""}
              />
              <TextField
                label="Sharp Ratio"
                variant="outlined"
                fullWidth
                margin="normal"
                name="sharpRatio"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, sharpRatio: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.sharpRatio || ""}
              />
              <TextField
                label="Sortino"
                variant="outlined"
                fullWidth
                margin="normal"
                name="sortino"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, sortino: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.sortino || ""}
              />
            </div>
            <div className="mt-4">
              <Button
                onClick={handleAddAIF}
                color="primary"
                variant="contained"
                sx={{
                  mr: 1,
                }}
                disabled={!formData.amc || !formData.productName}
              >
                Submit
              </Button>
            </div>
          </Box>
        </Box>
      </Modal>
      <ConfirmDialog
        title="Delete AIF"
        message="Are you sure you want to delete this AIF?"
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        onDelete={handleDeleteAIF}
      />
    </>
  );
};

export default AIFListing;
